import _ from 'lodash';
import { change, destroy } from 'redux-form';
import { replace } from 'react-router-redux';

import { TransactionEnum } from 'Common/FieldEnum';
import { TRANSACTION_FORM_NAME } from 'Common/FormEnum';
import * as types from 'PointOfSale/modules/queue/state/types';
import * as IndividualServingActions from 'PointOfSale/modules/individualServing/state/actions';
import * as POSSelectors from 'PointOfSale/selectors';
import * as CommonStateSelectors from 'State/selectors';
import {
  POS_ROUTES,
  QueuedDirectionEnum,
  SymbolIndicatorEnum,
  INVALID_STUDENT,
} from 'PointOfSale/modules/common/constants';
import {
  addAllergensProhibitedItems,
  isGenericAccount,
} from 'PointOfSale/modules/common/helpers';
import { error } from 'State/actions';
import { getOnline } from 'State/selectors';
import { fetchStudent } from 'PointOfSale/state/actions';

import { getBoughtItems } from '../../../helpers/transactions';

export const addToQueue = (
  profile,
  queuedDirection = QueuedDirectionEnum.None
) => async (dispatch, getState, MPWWebAPI) => {
  const state = getState();
  const queue = POSSelectors.getQueue(state);
  const autoServe = POSSelectors.getAutoServe(state);

  const selectedSiteDetails = POSSelectors.getSelectedSiteDetails(state);

  const isOnline = getOnline(state);
  const hasMealCounterSession = POSSelectors.hasMealCounterSession(state);

  const routing = state.routing;
  const path = routing.location.pathname;

  const isInvalidStudent = profile.Id === INVALID_STUDENT.Id;
  profile = _.cloneDeep(profile);
  profile.symbolIndicator = [];

  if (selectedSiteDetails.Id !== profile.SiteId && !isGenericAccount(profile)) {
    profile.symbolIndicator.push({
      type: SymbolIndicatorEnum.VisitingStudent,
      isOpen: false,
    });
  }

  if (queuedDirection !== QueuedDirectionEnum.None) {
    profile.symbolIndicator.push({
      type: SymbolIndicatorEnum.ServingDirection,
      value: queuedDirection,
      message: null,
      isOpen: false,
    });
  } else {
    profile.symbolIndicator.push({
      type: SymbolIndicatorEnum.ManuallyKeyed,
      value: null,
      message: null,
      isOpen: false,
    });
  }

  if (isInvalidStudent) {
    profile.symbolIndicator.push({
      type: SymbolIndicatorEnum.InvalidStudent,
      value: null,
      message: `Invalid Student - ${profile.Id}`,
      isOpen: true,
    });
  }

  if (!isInvalidStudent && isOnline) {
    try {
      const [student, err] = await dispatch(
        fetchStudent(profile.IdSeed, {
          fetch: true,
          picture: true,
        })
      );

      if (err) {
        throw err;
      }

      profile = {
        ...profile,
        ...student,
      };
    } catch (err) {
      dispatch(error(types.ADD_TO_QUEUE_ERROR, err));
    }
  }

  addAllergensProhibitedItems(profile, profile.symbolIndicator);

  /** No student in queue, set current profile */
  if (queue.length === 0) {
    dispatch(IndividualServingActions.setProfile(profile));
    dispatch(
      change(
        TRANSACTION_FORM_NAME,
        TransactionEnum.StudentIdSeed,
        profile.IdSeed
      )
    );
    if (!autoServe && !hasMealCounterSession) {
      dispatch(replace(`${POS_ROUTES.QUEUE}/${profile.Id}`));
    }

    if (path !== POS_ROUTES.QUEUE) {
      dispatch(IndividualServingActions.reset());
      // Only add the quick serve item if not on the queue page otherwise
      // the prohibited items message will be the triggered as soon as
      // the student is added
      dispatch(IndividualServingActions.addQuickServeItem());
    }
  }

  dispatch({
    type: types.ADD_TO_QUEUE,
    payload: profile,
  });
};

export const removeFromQueue = (index = 0) => (dispatch, getState) => {
  const state = getState();
  const queue = POSSelectors.getQueue(state);
  dispatch(destroy(TRANSACTION_FORM_NAME));
  const nextInLine = _.get(queue, '1');
  const autoServe = POSSelectors.getAutoServe(state);

  let nextRoute = POS_ROUTES.QUEUE_NEW;

  if (nextInLine) {
    dispatch(IndividualServingActions.setProfile(nextInLine));
    dispatch(
      change(
        TRANSACTION_FORM_NAME,
        TransactionEnum.StudentIdSeed,
        nextInLine.IdSeed
      )
    );
    nextRoute = autoServe
      ? POS_ROUTES.QUEUE
      : `${POS_ROUTES.QUEUE}/${nextInLine.Id}`;
  } else {
    dispatch(IndividualServingActions.clearCurrentProfile());
    nextRoute = autoServe ? POS_ROUTES.QUEUE : POS_ROUTES.QUEUE_NEW;
  }

  dispatch(replace(nextRoute));

  if (nextInLine && !autoServe) {
    dispatch(IndividualServingActions.reset());
    dispatch(IndividualServingActions.addQuickServeItem());
  }

  dispatch({
    type: types.REMOVE_FROM_QUEUE,
    payload: index,
  });
};

export const removeFromQueueAudit = () => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  const state = getState();

  if (
    !(
      CommonStateSelectors.getOnline(state) &&
      CommonStateSelectors.getServerUp(state)
    )
  ) {
    return;
  }

  const profile = POSSelectors.getProfile(state);
  const currentTransaction = POSSelectors.getCurrentTransaction(state);
  const userInfo = state.SecurityState.userInfo;

  const transactionBoughtItems = getBoughtItems(currentTransaction);
  const profileId = _.get(profile, 'Id', '');

  const data = {
    UserId: String(userInfo.id),
    EffectiveDate: currentTransaction.WorkingDT,
    PrimarySiteId: currentTransaction.PrimarySiteId,
    Items: transactionBoughtItems.map((i) => ({
      ItemId: i.ItemId,
      Description: i.Description,
      Price: i.Price,
      Quantity: i.Qty,
    })),
    CheckNumber: _.get(currentTransaction, 'CheckNumber'),
    AmountPaid: currentTransaction.AmountPaid,
    StudentId: profileId,
  };

  try {
    await MPWWebApi.postGenericInsertUpdate(`audit/write`, data);
  } catch (error) {
    // Do nothing with an error.
  }
};

export const resetQueue = () => ({
  type: types.RESET_QUEUE,
});

export const changeQueueStatus = (status) => ({
  type: types.CHANGE_QUEUE_STATUS,
  payload: status,
});

/** Moves selected index to front of queue */
export const shuffleQueue = (index) => (dispatch, getState) => {
  const state = getState();
  const queue = POSSelectors.getQueue(state);
  dispatch(destroy(TRANSACTION_FORM_NAME));
  if (queue.length < 1) {
    dispatch(IndividualServingActions.clearCurrentProfile());
    return;
  }

  const profile = queue[index];

  dispatch(IndividualServingActions.setProfile(profile));

  dispatch(
    change(TRANSACTION_FORM_NAME, TransactionEnum.StudentIdSeed, profile.IdSeed)
  );
  const id = profile.Id;
  dispatch(replace(`${POS_ROUTES.QUEUE}/${id}`));
  dispatch(IndividualServingActions.addQuickServeItem());
  dispatch({
    type: types.SHUFFLE_QUEUE,
    payload: index,
  });
};

export const setCurrentProfile = (profile) => async (dispatch, getState) => {
  const state = getState();
  const autoServe = POSSelectors.getAutoServe(state);

  const hasMealCounterSession = POSSelectors.hasMealCounterSession(state);

  const routing = state.routing;
  const path = routing.location.pathname;

  dispatch(IndividualServingActions.setProfile(profile));
  dispatch(
    change(TRANSACTION_FORM_NAME, TransactionEnum.StudentIdSeed, profile.IdSeed)
  );
  if (!autoServe && !hasMealCounterSession) {
    dispatch(replace(`${POS_ROUTES.QUEUE}/${profile.Id}`));
  }

  if (path !== POS_ROUTES.QUEUE) {
    dispatch(IndividualServingActions.reset());
    // Only add the quick serve item if not on the queue page otherwise
    // the prohibited items message will be the triggered as soon as
    // the student is added
    dispatch(IndividualServingActions.addQuickServeItem());
  }
};



// WEBPACK FOOTER //
// ./src/pages/pointofsale/modules/queue/state/actions.js